import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Autocomplete, Box, Button, Tabs, Tab, TextField, Paper, Grid, Stack } from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import get from 'lodash/get';

import { createDevice, deleteDevice, updateDevice } from 'api/devices';
import InputLabel from 'components/InputLabel';
import { createThing, deleteThing, updateThing } from 'store/objects/actions';
import { OBJECT_CATEGORIES } from 'enum/object';
import { DEFAULT_LOCATION, LOCATION_TYPE } from 'enum/location';

import DescriptionTab from '../components/DescriptionTab';
import { validationSchema } from './validationSchema';

const ThingForm = ({ object, onClose }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { cusID: customerUUID, ecoID: ecosystemUUID } = useParams();
  const groups = useSelector((state) => state.ecosystems.dictionaries.groups);

  const handleSave = async (values) => {
    try {
      const payload = {
        name: values.name,
        type: values.type,
        assetValue: values.assetValue,
        description: values.description,
        profileGroup: values.profileGroup,
        category: values.category,
        geolocation: {
          latitude: values.lat,
          longitude: values.lng,
        },
      };
      if (object?.uuid) {
        const result = await updateDevice({ customerUUID, ecosystemUUID, payload, uuid: object.uuid });
        result.profileGroup = groups.find((group) => group.uuid === values.profileGroup);
        dispatch(updateThing({ ecosystemUUID, result }));
        enqueueSnackbar(`${values.name} (Thing) has been updated`, { variant: 'success' });
      } else {
        const result = await createDevice({ customerUUID, ecosystemUUID, payload });
        result.profileGroup = groups.find((group) => group.uuid === values.profileGroup);
        dispatch(createThing({ ecosystemUUID, result }));
        enqueueSnackbar('New Thing has been created', { variant: 'success' });
      }
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error saving object:', error);
      dispatch({ type: 'THING_FAILURE', payload: { message: error } });
    }
  };

  const [selectedTab, setSelectedTab] = useState(1);
  const { values, errors, touched, handleChange, handleSubmit, isSubmitting } = useFormik({
    initialValues: {
      name: get(object, 'name', ''),
      type: 'device',
      assetValue: get(object, 'assetValue', 1),
      description: get(object, 'description', ''),
      profileGroup: get(object, 'profileGroup.uuid', groups[0]?.uuid ?? ''),
      category: get(object, 'category', ''),
      // eslint-disable-next-line no-nested-ternary
      location: object
        ? object.geolocation
          ? LOCATION_TYPE.COORDINATES
          : LOCATION_TYPE.UNDEFINED
        : LOCATION_TYPE.COORDINATES,
      lat: get(object, 'geolocation.latitude', DEFAULT_LOCATION.LAT),
      lng: get(object, 'geolocation.longitude', DEFAULT_LOCATION.LNG),
    },
    validationSchema,
    onSubmit: handleSave,
  });

  useEffect(() => {
    setSelectedTab(1);
  }, [isSubmitting]);

  const handleDelete = async () => {
    if (!object?.uuid) return;
    try {
      await deleteDevice({ customerUUID, ecosystemUUID, uuid: object.uuid });
      dispatch(deleteThing({ ecosystemUUID, uuid: object.uuid }));
      enqueueSnackbar(`${object.name} (Thing) has been removed`, { variant: 'success' });
      onClose();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log('Error deleting object:', error);
      dispatch({ type: 'THING_DELETE_FAILURE', payload: { message: error } });
    }
  };

  return (
    <Box sx={{ px: 4, pt: 2, pb: 3 }}>
      <Tabs value={selectedTab} onChange={(_, val) => setSelectedTab(val)} sx={{ mb: 2.5 }}>
        <Tab
          label="Configuration"
          data-testid="tab_configuration"
          value={1}
          sx={{ fontSize: 16, fontWeight: 600, textTransform: 'none' }}
        />
        <Tab
          label="Descriptors"
          data-testid="tab_descriptors"
          value={2}
          sx={{ fontSize: 16, fontWeight: 600, textTransform: 'none' }}
        />
      </Tabs>
      <Paper elevation={0} sx={{ borderRadius: 2 }}>
        <Box sx={{ p: 4 }}>
          {selectedTab === 1 && (
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <InputLabel required>Name</InputLabel>
                    <TextField
                      fullWidth
                      name="name"
                      variant="standard"
                      placeholder="Name"
                      value={values.name}
                      onChange={handleChange}
                      error={Boolean(touched.name && errors.name)}
                      helperText={!!touched.name && errors.name}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel required>Category</InputLabel>
                    <Autocomplete
                      options={OBJECT_CATEGORIES}
                      getOptionLabel={(option) => option.label}
                      value={OBJECT_CATEGORIES.find((cat) => cat.value === values.category)}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          <img src={option.icon} alt={option.label} style={{ width: 24, height: 24, marginRight: 8 }} />
                          {option.label}
                        </Box>
                      )}
                      onChange={(event, newValue) =>
                        handleChange({ target: { name: 'category', value: newValue.value } })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            id: 'select-category_thing',
                          }}
                          variant="standard"
                          placeholder="Select category"
                          error={Boolean(touched.category && errors.category)}
                          helperText={!!touched.category && errors.category}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <InputLabel>Operational Importance</InputLabel>
                    <TextField
                      fullWidth
                      variant="standard"
                      type="number"
                      name="assetValue"
                      value={values.assetValue}
                      onChange={handleChange}
                      error={Boolean(touched.assetValue && errors.assetValue)}
                      helperText={!!touched.assetValue && errors.assetValue}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={4}>
                  <Grid item xs={12}>
                    <InputLabel required>Profile Group</InputLabel>
                    <Autocomplete
                      options={groups}
                      getOptionLabel={(option) => option.name}
                      value={groups.find((group) => group.uuid === values.profileGroup) || null}
                      onChange={(event, newValue) =>
                        handleChange({ target: { name: 'profileGroup', value: newValue.uuid } })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            id: 'select-profile_group_thing',
                          }}
                          variant="standard"
                          placeholder="Select profile group"
                          error={Boolean(touched.profileGroup && errors.profileGroup)}
                          helperText={!!touched.profileGroup && errors.profileGroup}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField disabled label="Type" fullWidth value="Thing" />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          {selectedTab === 2 && (
            <DescriptionTab values={values} errors={errors} touched={touched} onChange={handleChange} />
          )}
        </Box>
      </Paper>
      <Box sx={{ pt: 3 }}>
        <Stack direction="row-reverse" justifyContent="space-between">
          <Button data-testid="btn-save_thing" variant="contained" onClick={handleSubmit} sx={{ minWidth: 90 }}>
            {object?.uuid ? 'Save' : 'Add'}
          </Button>
          {object?.uuid && (
            <Button
              data-testid="btn-delete_thing"
              variant="contained"
              color="error"
              onClick={handleDelete}
              sx={{ minWidth: 90 }}
            >
              Delete
            </Button>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

ThingForm.propTypes = {
  object: PropTypes.object,
  onClose: PropTypes.func,
};

export default ThingForm;
